import { renderUrlOrValue } from "@shared/utils";
import type { PropertyDetailRowProps } from "@survey-types/survey.comparison.types";
import classNames from "classnames";
import { Fragment, type ReactNode } from "react";

/**
 * Render split content lines wrapped in a pre tag
 */
const renderSplitContent = (
  lines: string[],
  fieldKey: string | undefined | null,
  charsPerLine: number,
): ReactNode => {
  return (
    <>
      {lines.map((line, index) => {
        // Check if line ends with \n (paragraph boundary marker)
        const endsWithNewline = line.endsWith("\n");
        // Remove the \n for rendering
        const cleanLine = endsWithNewline ? line.slice(0, -1) : line + " ";

        return (
          <Fragment key={"f" + index}>
            {renderUrlOrValue(cleanLine, fieldKey, charsPerLine)}
            {endsWithNewline ? <div /> : null}
          </Fragment>
        );
      })}
    </>
  );
};

/**
 * Property Detail Row Component
 *
 * Performance optimizations:
 * 1. Receives pre-processed property values instead of full property objects
 * 2. Eliminates the need for value extraction logic in the component
 * 3. Reduces memory footprint by only receiving necessary data
 * 4. React compiler handles automatic optimization (no manual memoization needed)
 * 5. Simplifies component logic and improves rendering performance
 */
const PropertyDetailRow = ({
  label,
  propertyValues,
  maxProperties,
  fieldKey,
  rowIndex,
  className,
  isContinued = false,
  mode,
}: PropertyDetailRowProps) => {
  // Get character limit based on orientation mode - aligned with pagination constants
  const charsPerLine = mode === "Landscape" ? 18 : 25;
  // Format label with continuation if needed
  // React compiler automatically optimizes this function
  const getDisplayLabel = () => {
    return isContinued ? `${label}, continued` : label;
  };

  // Odd rows (1, 3, 5...) get background styling based on 0-indexed rowIndex
  const shouldHaveBackground = rowIndex % 2 === 1;

  return (
    <div
      className={classNames("grid py-1", className, {
        "bg-secondary-lightest": shouldHaveBackground,
      })}
      style={{
        gridTemplateColumns: `150px repeat(${maxProperties}, minmax(0, 1fr))`,
      }}
    >
      {/* Label Cell */}
      <div className="text-tiny text-data break-normal wrap-break-word hyphens-auto pl-2">
        {getDisplayLabel()}
      </div>

      {/* Pre-processed Property Value Cells */}
      {propertyValues.map((propertyValue) => (
        <div
          key={propertyValue.propertyId}
          className={classNames(
            "text-tiny px-2 leading-3.5 break-normal wrap-break-word hyphens-auto text-data",
            {
              "font-semibold": fieldKey === "name",
            },
          )}
        >
          {Array.isArray(propertyValue.value)
            ? // Handle split content lines
              renderSplitContent(propertyValue.value, fieldKey, charsPerLine)
            : renderUrlOrValue(propertyValue.value, fieldKey, charsPerLine)}
        </div>
      ))}
      {/* Fill empty columns if less than max properties */}
      {Array.from({ length: maxProperties - propertyValues.length }).map(
        (_, index) => (
          <div key={`empty-${index}`} className="px-2" />
        ),
      )}
    </div>
  );
};

export default PropertyDetailRow;
